
/* eslint-disable @typescript-eslint/naming-convention */
import Vue, { PropType } from 'vue';

import DurationInput from '@/components/input/DurationInput.vue';
import UnitInput from '@/components/input/UnitInput.vue';
import Modal from '@/components/modal/Modal.vue';
import ToolTip from '@/components/common/ToolTip.vue';
import { between, minLength, minValue, required, requiredIf } from 'vuelidate/lib/validators';
import {
	RuleAddEdit,
	RuleTemplate,
	RulespecMovement,
	RulespecDefault,
	TemplateSpec,
	TemplatespecInstruction,
	RulespecLightingSettings,
	TemplatespecMovementSettings,
	RulespecInitial,
} from '@/interfaces/rules';
import { Validation } from 'vuelidate/vuelidate';

interface RuleForm {
	name: string;
	description?: string;
	rulespec: TemplateSpec;
}

function getDefaultRuleForm(): RuleForm {
	return {
		name: '',
		description: '',
		rulespec: {
			rules: [
				{ type: 'movement', value: 100, timeout: 60, relative: true },
				{ type: 'movement', value: 50, timeout: 60, relative: true },
				{ type: 'default', value: 0, relative: false },
				{ type: 'movement_settings', sensors: '${sensors}' },
				{ type: 'lighting_settings', ramp_up_time: 10, ramp_down_time: 10 },
				{ type: 'initial', value: 100, timeout: 300 },
			] as TemplatespecInstruction[],
		},
	};
}

export default Vue.extend({
	components: {
		Modal,
		ToolTip,
		DurationInput,
		UnitInput,
	},
	data() {
		return {
			ruleForm: getDefaultRuleForm(),
			editMode: false,
		} as {
			ruleForm: RuleForm;
			editMode: boolean;
		};
	},
	validations: {
		ruleForm: {
			name: {
				required,
				minLength: minLength(2),
			},
			description: {},
			rulespec: {
				rules: {
					$each: {
						value: {
							between: between(-1, 100),
							required: requiredIf((rulePart) =>
								['movement', 'default', 'initial'].includes(rulePart.type)
							),
						},
						timeout: {
							minValue: minValue(60),
							required: requiredIf((rulePart) => ['movement', 'initial'].includes(rulePart.type)),
						},
						ramp_up_time: {
							minValue: minValue(0),
							required: requiredIf((rulePart) => rulePart.type === 'lighting_settings'),
						},
						ramp_down_time: {
							minValue: minValue(0),
							required: requiredIf((rulePart) => rulePart.type === 'lighting_settings'),
						},
					},
				},
			},
		},
	},
	props: {
		oldRule: Object as PropType<RuleTemplate | null>,
	},
	watch: {
		oldRule: {
			deep: true,
			handler() {
				if (this.oldRule !== null) {
					this.editMode = true;
					const [oldActive, oldStandby, oldDefault, oldMovementSettings, oldLightingSettings] =
						this.getRuleInstructions(this.oldRule.rulespec[0]);
					const [defaultMovement, defaultLighting] = this.getRuleInstructions(
						getDefaultRuleForm().rulespec
					).slice(3, 5);
					this.ruleForm = {
						name: this.oldRule.name,
						description: this.oldRule.description,
						rulespec: {
							rules: [
								{
									...oldActive,
									relative: oldActive.value > 0,
								},
								{
									...oldStandby,
									timeout: oldStandby.timeout - oldActive.timeout,
									relative: oldStandby.value > 0,
								},
								{
									type: 'default',
									value: Math.max(0, oldDefault.value),
									relative: oldDefault.value > 0,
								},
								{ ...oldMovementSettings } || defaultMovement,
								{ ...oldLightingSettings } || defaultLighting,
								{
									type: 'initial',
									value: 100,
									timeout: 300,
								},
							],
						},
					};
				} else {
					this.editMode = false;
					this.resetRuleModal();
				}
			},
		},
	},
	methods: {
		closeRuleModal(): void {
			this.$bvModal.hide('modal-rule');
		},

		confirmRuleModal(): void {
			this.$v.ruleForm.$touch();

			if (this.$v.ruleForm.$anyError) {
				return;
			}

			const [active, standby, default_, movementSettings, lightingSettings] =
				this.getRuleInstructions(this.ruleForm.rulespec);
			const rule: RuleAddEdit = {
				name: this.ruleForm.name,
				description: this.ruleForm.description,
				rulespec: [
					{
						rules: [
							{
								...active,
								relative: active.value > 0,
							},
							{
								...standby,
								timeout: active.timeout + standby.timeout,
								relative: standby.value > 0,
							},
							{
								type: 'default',
								value: default_.value === 0 ? -1 : Math.min(default_.value, 100),
								relative: default_.value > 0,
							},
							movementSettings,
							lightingSettings,
							{
								type: 'initial',
								value: 100,
								timeout: 300,
							},
						],
					},
				],
				fk_rule_type: 'lighting',
				fk_client: undefined,
				fk_site: undefined,
			};

			if (this.editMode) {
				rule.fk_client = this.oldRule?.fk_client;
				rule.fk_site = this.oldRule?.fk_site;
				this.$emit('editRule', this.oldRule?.id, rule);
			} else {
				this.$emit('newRule', rule);
			}
		},

		resetRuleModal(): void {
			if (!this.editMode) {
				this.ruleForm = getDefaultRuleForm();
			}

			this.$v.ruleForm.$reset();
		},

		validateState(validation: Validation): boolean | null {
			const { $dirty, $error }: { $dirty: boolean; $error: boolean } = validation;
			return $dirty ? !$error : null;
		},

		getRuleInstructions(
			rulespec: TemplateSpec
		): [
			RulespecMovement,
			RulespecMovement,
			RulespecDefault,
			TemplatespecMovementSettings,
			RulespecLightingSettings,
			RulespecInitial
		] {
			return [
				rulespec.rules[0],
				rulespec.rules[1],
				rulespec.rules[2],
				rulespec.rules[3],
				rulespec.rules[4],
				rulespec.rules[5],
			] as [
				RulespecMovement,
				RulespecMovement,
				RulespecDefault,
				TemplatespecMovementSettings,
				RulespecLightingSettings,
				RulespecInitial
			];
		},
	},
});
